import { useEffect } from 'react';
import i18n from '@/i18n';

/****************
 * "You have unsaved progress" — for in-app navigation.
 *
 * Guillaume, 2026-09-04: "When I create a strategy and I exit the page (like go
 * to another section) before it's done, just have a pop-up saying that the
 * progress will be lost if I exit the page."
 *
 * ── Why this is a module-level register rather than a router feature ────────
 *
 * React Router's own `useBlocker` only exists on a data router
 * (`createBrowserRouter`). This app mounts `<BrowserRouter>` with a `<Routes>`
 * tree, so the hook is not available, and switching router styles to add a
 * confirmation dialog would be a large change to every route for one message.
 *
 * So the page that has something to lose REGISTERS a guard, and the navigation
 * that would discard it ASKS. Two small pieces, no router change, and a page
 * that forgets to register simply behaves as it did before.
 *
 * ── The browser's own dialog is kept for tab close ──────────────────────────
 *
 * `beforeunload` covers closing the tab and hitting reload, which no in-app
 * handler can intercept. It cannot be styled or worded — browsers show their
 * own text — so it is a backstop, not the feature.
 ****************/

type Guard = () => boolean;

/** The guards currently armed, by owner. A Map so a page replacing its own
 *  guard cannot leave a stale one behind. */
const guards = new Map<string, Guard>();

/** True when anything on screen would lose work. */
export function hasUnsavedWork(): boolean {
  for (const guard of guards.values()) {
    try {
      if (guard()) return true;
    } catch {
      /* A broken guard must not make the app unnavigable. */
    }
  }
  return false;
}

/**
 * Ask before leaving, when there is something to lose.
 *
 * Returns true to proceed. Uses `window.confirm` deliberately: it is the one
 * dialog that can answer synchronously inside a click handler, and a navigation
 * decision cannot wait for a React state round-trip without letting the
 * navigation happen first.
 */
export function confirmLeave(
  message = i18n.t('messages:unsaved.leave'),
): boolean {
  if (!hasUnsavedWork()) return true;
  return window.confirm(message);
}

/**
 * Register a guard for as long as the component is mounted.
 *
 * `isDirty` is read at navigation time, not at registration time, so it always
 * reflects the current state rather than the state when the effect ran.
 */
export function useUnsavedGuard(owner: string, isDirty: () => boolean) {
  useEffect(() => {
    guards.set(owner, isDirty);

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty()) return;
      e.preventDefault();
      // Required by older browsers; the string itself is ignored by all of them.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      guards.delete(owner);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner]);
}
